// stages.js: Zero-style stages for the scroll film (v6, shell agent).
//
// Concept-agnostic: STAGE_LIST is the only part that knows the film, so a new art direction swaps the list, not the
// code. At a stage point the film HOLDS: Lenis stops, the scroll settles on the point, and a ring prompt names one
// gesture (hold, tap or drag). The gesture plays a 2-3 s reward: that chapter's world slot gets params.stage 0 -> 1
// (a world that knows the param draws it; the others ignore it), the grade swells, a lens pulses on the ring, a sound
// cue plays and the counter ticks (STAGE N/M in the prompt, pips + n/M in the HUD). Then the film runs on.
//
// NO DEAD ENDS
//  - ~1.2 screens of further wheel / touch scrolling pass a stage (wheel deltas arrive capped by main.js capWheel,
//    so one flick can't), and so do Space and Enter; ArrowDown / PageDown count as scrolling;
//  - scrolling back up (or ArrowUp / PageUp / Home) releases a held stage; it re-arms when you come forward again;
//  - reduced motion auto-passes after a short beat; a link or programmatic jump releases a held stage (jumped());
//  - a passed stage never re-locks; a resize or rotation (main.js keepFilm) re-snaps the film to the stage point;
//  - main.js wraps tick() and disposes the module on any error, which restarts Lenis.
//
// spec = { id, ch, atP, gesture: 'hold' | 'drag' | 'tap', prompt, promptMouse?, rewardS, anchors?, param?, drive?, lens?, gate? }
//   ch       chapter index (or a CHAPTERS key); atP = chapter progress where the film holds. Keep it between copy
//            beats (a clean moment); it is clamped out of the cut windows at runtime (film.cutSpan)
//   anchors  world anchor names for the ring (the first visible one wins, else the screen centre)
//   param    the slot param the reward animates 0 -> 1 (default 'stage'); drive: a slot param a drag drives live (0..1)
//   lens     reward lens strength (default 1); gate: the existing hold gate (gate.js), passed at ignition
import { clamp } from './util.js';
import { CHAPTERS } from './film.js';

// v5.8 film (5 chapters): the gate is stage 1, then one stage per chapter up to the black hole, each between copy beats
export const STAGE_LIST = [
  { id: 'ignite', gate: true, ch: 'ignite', prompt: 'Hold to ignite' },
  { id: 'pull', ch: 'ignite', atP: 0.51, gesture: 'hold', prompt: 'Hold to pull it in', rewardS: 2.4, anchors: ['star', 'protostar', 'core', 'center'] },
  { id: 'city', ch: 'home', atP: 0.7, gesture: 'tap', prompt: 'Tap your city', promptMouse: 'Click your city', rewardS: 2.4, anchors: ['city', 'you', 'home'] },
  { id: 'across', ch: 'scale', atP: 0.72, gesture: 'drag', prompt: 'Drag across the galaxy', rewardS: 2.6, anchors: [] }, // centred: the core is too bright to carry a ring
  // a second lens over the black hole read as a glitch (a pinched knot in the shadow): here the light drains instead
  { id: 'fall', ch: 'gravity', atP: 0.76, gesture: 'hold', prompt: 'Hold to fall in', rewardS: 2.8, lens: 0, swell: { exposure: -0.5, vignette: 1.2, bloomK: 0.25 }, anchors: ['center', 'hole', 'bh', 'shadow'] },
];

const SWELL = { exposure: 0.28, bloomK: 0.7, sat: 0.08 }; // a reward's grade at its peak (multipliers); spec.swell overrides
const HOLD_S = 1.1;          // seconds of holding
const DRAG_W = 0.34;         // share of the viewport width to drag across
const TAP_MS = 600, TAP_PX = 24;
const PUSH = 1.2;            // screens of further scrolling that pass a stage
const BACK = 0.12;           // screens of scrolling back that release it
const AUTO_S = 0.9;          // reduced motion: the prompt shows this long, then the stage passes by itself
const RC = 289.03;           // 2 * pi * 46: the ring's circumference in its 100-unit viewBox
const ease = (k) => k * k * (3 - 2 * k);
const inOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

const CSS = `
.stg { position: fixed; left: 0; top: 0; width: 104px; height: 104px; margin: -52px 0 0 -52px; z-index: 40; pointer-events: none; opacity: 0;
  transition: opacity .6s cubic-bezier(.16,1,.3,1); will-change: transform; }
.stg.on { opacity: 1; }
.stg svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; transform: rotate(-90deg) scale(var(--s, 1)); filter: drop-shadow(0 0 5px rgba(0,0,0,.6)); }
.stg-bg { fill: none; stroke: rgba(239,237,232,.78); stroke-width: 1.1; transform-origin: 50% 50%; animation: stg-breathe 2.4s ease-in-out infinite; }
.stg-fg { fill: none; stroke: var(--acc, #8FE9FF); stroke-width: 2.2; stroke-linecap: round; stroke-dasharray: 289.03; stroke-dashoffset: 289.03; }
.stg-dot { position: absolute; left: 50%; top: 50%; width: 6px; height: 6px; margin: -3px; border-radius: 50%; background: var(--acc, #8FE9FF);
  box-shadow: 0 0 12px var(--acc, #8FE9FF); }
.stg::before { content: ""; position: absolute; inset: 0; border-radius: 50%; border: 1px solid var(--acc, #8FE9FF); opacity: 0;
  animation: stg-ping 2.4s cubic-bezier(.16,1,.3,1) infinite; }
.stg[data-g="drag"]::after { content: ""; position: absolute; left: -56px; right: -56px; top: 50%; height: 1px;
  background: linear-gradient(90deg, rgba(239,237,232,0), rgba(239,237,232,.7) 32%, rgba(239,237,232,.7) 68%, rgba(239,237,232,0)); }
.stg-lbl { position: absolute; left: 50%; bottom: calc(100% + 16px); width: max-content; max-width: min(78vw, 420px); margin: 0;
  transform: translateX(calc(-50% + var(--lx, 0px))); text-align: center; color: var(--ink, #EFEDE8); isolation: isolate;
  font: 500 11px/1.45 var(--mono, ui-monospace, monospace); letter-spacing: .22em; text-transform: uppercase; text-shadow: 0 1px 14px rgba(0,0,0,.85); }
/* a soft dark bed under the words: the prompt must read over a white galaxy core or a teal sky */
.stg-lbl::before { content: ""; position: absolute; inset: -24px -52px; z-index: -1; background: radial-gradient(closest-side, rgba(4,5,10,.5), rgba(4,5,10,.2) 64%, rgba(4,5,10,0)); }
.stg-lbl b { display: block; margin-bottom: 7px; font-weight: 500; font-size: 9.5px; letter-spacing: .32em; color: var(--acc, #8FE9FF); }
.stg-lbl small { display: block; margin-top: 7px; font-size: 9px; letter-spacing: .18em; color: rgba(239,237,232,.74); }
.stg-lbl small:empty { display: none; }
.stg.won .stg-bg, .stg.won::before { animation: none; opacity: 0; }
.stg.won svg { transform: rotate(-90deg) scale(2.3); opacity: 0; transition: transform 1.3s cubic-bezier(.16,1,.3,1), opacity 1.3s ease; }
.stg.won .stg-dot { transform: scale(4); opacity: 0; transition: transform 1s cubic-bezier(.16,1,.3,1), opacity 1s ease; }
@keyframes stg-breathe { 50% { transform: scale(.94); } }
@keyframes stg-ping { 0% { transform: scale(.7); opacity: .7; } 100% { transform: scale(1.5); opacity: 0; } }
body.stg-on { touch-action: none; }
body.stg-on .labels, body.stg-on .scroll-hint { opacity: 0 !important; transition: opacity .4s; }
.hud-stg { display: inline-flex; align-items: center; gap: 5px; margin-left: 4px; }
.hud-stg i { display: block; width: 5px; height: 5px; border-radius: 50%; box-shadow: inset 0 0 0 1px rgba(239,237,232,.45);
  transition: background-color .5s, box-shadow .5s, transform .6s cubic-bezier(.16,1,.3,1); }
.hud-stg i.on { background: var(--acc, #8FE9FF); box-shadow: 0 0 8px var(--acc, #8FE9FF); }
.hud-stg i.tick { transform: scale(2.2); }
.hud-stg b { margin-left: 5px; font-weight: 500; font-variant-numeric: tabular-nums; }
.stg-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (prefers-reduced-motion: reduce) { .stg-bg, .stg::before { animation: none; } }
`;

export class Stages {
  // d = { film, lenis, reduced, cue(name), slotFor(ch), params(slot), ready(slot), anchor(slot, names) -> [x, y] CSS px | null, list? }
  constructor(d) {
    this.d = d;
    this.body = document.body;
    this.list = (d.list || STAGE_LIST).map((s, i) => ({
      ...s, i, ch: typeof s.ch === 'string' ? CHAPTERS.findIndex((c) => c.key === s.ch) : s.ch,
      rewardS: s.rewardS || 2.4, param: s.param || 'stage', anchors: s.anchors || [],
      state: '', passed: false, out: false, how: '', t0: 0, rt0: -1, at: null,
    })).filter((s) => s.ch >= 0);
    this.total = this.list.length;
    this.cur = null; this.S = null; this.dead = false;
    this.clock = 0; this.userT = -1e9; this.prevU = -1; this.jump = false;
    this.push = 0; this.back = 0; this.charge = 0; this.dragX = 0; this.down = false; this.ptr = null;
    this.fx = { lens: 0, glow: 0, x: 0, y: 0 };
    this.xy = [innerWidth / 2, innerHeight * 0.4]; this.painted = false; this.lw = 0; this.lx = 0; this.fillQ = -1; this.sq = -1;
    this.touch = !!(window.matchMedia && matchMedia('(hover: none) and (pointer: coarse)').matches);
    for (const s of this.list) { if (s.gate) continue; const p = this.par(s); if (p && !Number.isFinite(p[s.param])) p[s.param] = 0; }
    this.build();
    this.bind();
  }

  par(s) { try { return this.d.params(this.d.slotFor(s.ch)) || null; } catch (e) { return null; } }
  count() { let n = 0; for (const s of this.list) if (s.passed) n++; return n; }
  rs(s) { return this.d.reduced ? 0.7 : s.rewardS; }
  // the stage point, kept out of the cut windows (the chapter's own progress, so PACE and resizes never move it)
  safeP(s) {
    const f = this.d.film, n = f.halves ? f.halves.length : 0;
    let a = 0.02, b = 0.98;
    if (s.ch > 0 && s.ch - 1 < n) a = f.cutSpan(s.ch - 1).inP + 0.03;
    if (s.ch < n) b = f.cutSpan(s.ch).outP - 0.03;
    return clamp(s.atP, a, Math.max(a, b));
  }
  yOf(s) { return this.d.film.yFor(s.ch, this.safeP(s)); }

  build() {
    if (!document.getElementById('stg-css')) { const st = document.createElement('style'); st.id = 'stg-css'; st.textContent = CSS; document.head.appendChild(st); }
    const r = this.ring = document.createElement('div');
    r.className = 'stg';
    r.setAttribute('aria-hidden', 'true');
    r.innerHTML = '<svg viewBox="0 0 100 100"><circle class="stg-bg" cx="50" cy="50" r="46"/><circle class="stg-fg" cx="50" cy="50" r="46"/></svg><i class="stg-dot"></i><p class="stg-lbl"><b></b><span></span><small></small></p>';
    document.body.appendChild(r);
    this.fg = r.querySelector('.stg-fg');
    this.lbl = r.querySelector('.stg-lbl');
    this.lB = this.lbl.querySelector('b'); this.lP = this.lbl.querySelector('span'); this.lS = this.lbl.querySelector('small');
    this.live = document.createElement('p');
    this.live.className = 'stg-sr';
    this.live.setAttribute('aria-live', 'polite');
    document.body.appendChild(this.live);
    // the counter: one pip per stage + n/M, in the HUD's chapter index
    const host = document.querySelector('#hud .hud-idx') || document.getElementById('hud') || document.body;
    const c = this.counter = document.createElement('span');
    c.className = 'hud-stg';
    c.setAttribute('aria-hidden', 'true');
    c.innerHTML = this.list.map(() => '<i></i>').join('') + `<b>0/${this.total}</b>`;
    host.appendChild(c);
    this.pipEls = Array.from(c.querySelectorAll('i'));
    this.num = c.querySelector('b');
  }

  pips(tick) {
    this.list.forEach((s, k) => {
      const el = this.pipEls[k];
      if (!el) return;
      el.classList.toggle('on', s.passed);
      if (tick === s) { el.classList.add('tick'); setTimeout(() => el.classList.remove('tick'), 650); }
    });
    this.num.textContent = `${this.count()}/${this.total}`;
  }

  bind() {
    const ui = (t) => t && t.closest && t.closest('a, button, input, label, select, textarea, form, .chrome, .interactive');
    addEventListener('pointerdown', (e) => {
      const s = this.cur;
      if (this.dead || !s || s.state !== 'prompt' || e.button > 0 || ui(e.target)) return;
      this.down = true;
      this.ptr = { id: e.pointerId, x: e.clientX, y: e.clientY, lx: e.clientX, mx: e.clientX, my: e.clientY, t: performance.now() };
    });
    addEventListener('pointermove', (e) => {
      const p = this.ptr, s = this.cur;
      if (!this.down || !p || !s || e.pointerId !== p.id) return;
      if (s.gesture === 'drag') {
        this.dragX += Math.abs(e.clientX - p.lx);
        if (s.drive) { const q = this.par(s); if (q) q[s.drive] = clamp(e.clientX / Math.max(1, innerWidth), 0, 1); }
      }
      p.lx = e.clientX; p.mx = e.clientX; p.my = e.clientY;
    }, { passive: true });
    const up = (e) => {
      const p = this.ptr, s = this.cur;
      if (!this.down || !p || e.pointerId !== p.id) return;
      this.down = false; this.ptr = null;
      if (s && s.state === 'prompt' && s.gesture === 'tap' && e.type === 'pointerup' && performance.now() - p.t < TAP_MS && Math.hypot(e.clientX - p.x, e.clientY - p.y) < TAP_PX) this.win(s, 'tap');
    };
    addEventListener('pointerup', up, { passive: true });
    addEventListener('pointercancel', up, { passive: true });
    addEventListener('blur', () => { this.down = false; this.ptr = null; });
    addEventListener('keydown', (e) => {
      if (this.dead) return;
      const k = e.key, t = e.target;
      if (k === 'ArrowDown' || k === 'PageDown' || k === ' ' || k === 'Spacebar' || k === 'End') this.userT = this.clock; // native keyboard scrolling can arm a stage
      const s = this.cur;
      if (!s || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && t.closest && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.closest('button, a, [contenteditable]'))) return; // a focused control keeps its keys
      if (k === ' ' || k === 'Spacebar' || k === 'Enter') {
        e.preventDefault();
        if (s.state === 'prompt') this.win(s, 'key'); else if (s.state === 'reward' && !e.repeat) this.release(s, 'skip');
      } else if (k === 'ArrowDown' || k === 'PageDown') {
        e.preventDefault();
        this.onScroll({ event: e, deltaY: (k === 'PageDown' ? 0.9 : 0.3) * this.d.film.vh });
      } else if ((k === 'ArrowUp' || k === 'PageUp' || k === 'Home') && s.state === 'prompt') this.release(s, 'back');
    });
  }

  // every Lenis virtual-scroll event (main.js capWheel): wheel deltas arrive capped, touch deltas raw
  onScroll(d) {
    if (this.dead || !d || !d.event) return;
    const dy = +d.deltaY || 0;
    const ty = d.event.type || '';
    if (ty.indexOf('touch') === 0 && Math.abs(+d.deltaX || 0) > Math.abs(dy)) return; // a sideways drag is a gesture, not scrolling
    if (dy !== 0) this.userT = this.clock;
    const s = this.cur;
    if (!s) return;
    const vh = Math.max(1, this.d.film.vh);
    if (s.state === 'prompt') {
      if (dy > 0) { this.push += dy / vh; this.back = Math.max(0, this.back - dy / vh); } else if (dy < 0) { this.back -= dy / vh; if (this.back >= BACK) this.release(s, 'back'); }
    } else if (s.state === 'reward' && dy > 0) {
      this.push += dy / vh;
      if (this.push >= 0.35) this.release(s, 'skip'); // scrolling during the reward: the film runs on at once (the reward keeps playing)
    }
  }

  // main.js: a link / programmatic jump. Releases a held stage synchronously, before the caller scrolls
  jumped() { this.jump = true; if (this.cur) this.release(this.cur, 'jump'); }

  grab(s) {
    const { lenis } = this.d;
    this.cur = s; s.state = 'prompt'; s.t0 = this.clock; s.out = false;
    this.push = 0; this.back = 0; this.charge = 0; this.dragX = 0; this.down = false; this.ptr = null;
    try { lenis.stop(); } catch (e) { /* ignore */ }
    lenis.scrollTo(this.yOf(s), { force: true, duration: 0.9, easing: (x) => 1 - Math.pow(1 - x, 3) });
    this.lB.textContent = `Stage ${s.i + 1}/${this.total}`;
    this.lP.textContent = (!this.touch && s.promptMouse) || s.prompt || '';
    this.lS.textContent = this.touch ? 'or keep scrolling' : 'or keep scrolling · Enter';
    this.ring.dataset.g = s.gesture || 'hold';
    this.ring.classList.remove('won');
    this.painted = false; this.lw = 0; this.fillQ = -1;
    this.ring.classList.add('on');
    this.body.classList.add('stg-on');
    this.live.textContent = `Stage ${s.i + 1} of ${this.total}. ${this.lP.textContent}. Or keep scrolling, or press Enter.`;
  }

  win(s, how) {
    if (s.passed) return;
    s.passed = true; s.how = how; s.state = 'reward'; s.rt0 = this.clock; s.at = this.xy.slice();
    this.charge = 1; this.push = 0;
    try { if (this.d.cue) this.d.cue('brand'); } catch (e) { /* sound is optional */ }
    this.lB.textContent = `Stage ${s.i + 1}/${this.total}`;
    this.lP.textContent = 'Unlocked';
    this.lS.textContent = '';
    this.lw = 0;
    this.ring.classList.add('won');
    this.pips(s);
    this.live.textContent = `Stage ${s.i + 1} of ${this.total} unlocked.`;
    if (how === 'scroll') this.release(s, 'scroll'); // already scrolling: the reward plays while the film runs on
  }

  release(s, why) {
    const { lenis, film } = this.d;
    if (this.cur === s) this.cur = null;
    if (s.passed) s.state = 'done'; else { s.state = ''; s.out = true; }
    this.down = false; this.ptr = null; this.charge = 0; this.push = 0; this.back = 0; this.dragX = 0;
    this.body.classList.remove('stg-on');
    this.ring.classList.remove('on');
    try { lenis.start(); } catch (e) { /* ignore */ }
    if (why === 'done' && !this.d.reduced) lenis.scrollTo(this.yOf(s) + 0.3 * film.vh, { force: true, duration: 1.8, easing: inOut }); // the film runs on
  }

  // per frame, from main.js tick() (real seconds, so a slow device never makes a hold longer)
  tick(dt, S) {
    if (this.dead) return;
    this.clock += dt;
    this.S = S;
    const { film, lenis } = this.d;
    const vh = Math.max(1, film.vh), tgt = lenis.targetScroll, u = tgt / vh, pu = this.prevU < 0 ? u : this.prevU;
    this.prevU = u;
    const jumped = this.jump; this.jump = false;
    for (const c of this.list) {
      if (c.gate) { if (!c.passed && S.lit) { c.passed = true; c.how = 'gate'; this.pips(c); } continue; }
      if (c.out && u < this.yOf(c) / vh - 0.25) c.out = false; // backed out of it: it may hold again next time
    }
    let s = this.cur;
    // arm: the visitor's own scrolling carries the target across a stage point (jumps, links and deep links never arm)
    if (!s && S.lit && !jumped && u > pu && u - pu < 1.5 && (this.clock - this.userT < 0.6 || lenis.isScrolling === 'native')) { // native = touch momentum / keys
      for (const c of this.list) {
        if (c.gate || c.passed || c.out) continue;
        const us = this.yOf(c) / vh;
        if (pu < us && u >= us && this.d.ready(this.d.slotFor(c.ch))) { this.grab(c); s = c; break; }
      }
    }
    if (s) {
      const ys = this.yOf(s);
      // stay on the stage point: a resize / rotation (keepFilm) or native momentum can move it a little
      const an = lenis.animate;
      if (!(an && an.isRunning) && Math.abs(lenis.targetScroll - ys) > 1.5) lenis.scrollTo(ys, Math.abs(lenis.targetScroll - ys) < 0.03 * vh ? { force: true, immediate: true } : { force: true, duration: 0.45 });
      if (s.state === 'prompt') {
        if (s.gesture === 'hold') this.charge = this.down ? Math.min(1, this.charge + dt / HOLD_S) : Math.max(0, this.charge - dt / 0.8);
        else if (s.gesture === 'drag') this.charge = clamp(this.dragX / (DRAG_W * Math.max(1, S.vw)), 0, 1);
        if (this.charge >= 1) this.win(s, s.gesture);
        else if (this.push >= PUSH) this.win(s, 'scroll');
        else if (this.d.reduced && this.clock - s.t0 > AUTO_S) this.win(s, 'auto');
      } else if (s.state === 'reward' && this.clock - s.rt0 >= this.rs(s)) this.release(s, 'done');
    }
    // rewards: params[param] 0 -> 1 on the slot (it stays at 1: a passed stage never re-locks) + the fx main.js applies
    this.fx.lens = 0; this.fx.glow = 0;
    for (const c of this.list) {
      if (c.gate || c.rt0 < 0) continue;
      const k = clamp((this.clock - c.rt0) / this.rs(c), 0, 1);
      const p = this.par(c);
      if (p) p[c.param] = ease(k);
      if (k < 1 && c.at) {
        const e = Math.pow(Math.sin(Math.PI * k), 2);
        if (e > this.fx.glow) { this.fx.glow = this.d.reduced ? 0.25 * e : e; this.fx.lens = this.d.reduced ? 0 : e * (c.lens === undefined ? 1 : c.lens); this.fx.swell = c.swell || SWELL; this.fx.x = c.at[0]; this.fx.y = c.at[1]; }
      }
    }
    this.paint(S, dt);
  }

  paint(S, dt) {
    const s = this.cur;
    if (!s) return;
    let tx, ty;
    if (this.down && this.ptr && s.state === 'prompt' && s.gesture !== 'tap') { tx = this.ptr.mx; ty = this.ptr.my; } else {
      const a = this.d.anchor(this.d.slotFor(s.ch), s.anchors);
      tx = a ? a[0] : S.vw * 0.5; ty = a ? a[1] : S.vh * 0.42;
    }
    // clear of the header (the label sits above the ring) and of the bottom copy
    const top = Math.max(200, S.vh * 0.22), bot = Math.max(top, S.vh * (S.phone ? 0.5 : 0.66));
    tx = clamp(tx, 64, Math.max(64, S.vw - 64)); ty = clamp(ty, top, bot);
    if (!this.painted) { this.xy[0] = tx; this.xy[1] = ty; this.painted = true; }
    const k = 1 - Math.exp(-(this.down ? 30 : 8) * dt);
    this.xy[0] += (tx - this.xy[0]) * k; this.xy[1] += (ty - this.xy[1]) * k;
    this.ring.style.transform = `translate3d(${this.xy[0].toFixed(1)}px, ${this.xy[1].toFixed(1)}px, 0)`;
    const sc = s.state === 'prompt' && this.down && s.gesture === 'hold' ? Math.round((1 - 0.18 * this.charge) * 500) / 500 : 1;
    if (sc !== this.sq) { this.ring.style.setProperty('--s', String(sc)); this.sq = sc; }
    const fill = s.state === 'prompt' ? Math.max(this.charge, Math.min(1, this.push / PUSH)) : 1;
    const q = Math.round(fill * 400) / 400;
    if (q !== this.fillQ) { this.fg.style.strokeDashoffset = (RC * (1 - q)).toFixed(2); this.fillQ = q; }
    // keep the label inside the viewport (measured once per text change)
    if (!this.lw) this.lw = this.lbl.offsetWidth || 1;
    const left = this.xy[0] - this.lw / 2;
    const lx = Math.round(left < 14 ? 14 - left : left + this.lw > S.vw - 14 ? S.vw - 14 - (left + this.lw) : 0);
    if (lx !== this.lx) { this.lbl.style.setProperty('--lx', `${lx}px`); this.lx = lx; }
  }

  // main.js: the grade with the reward's swell (the same object when no reward plays)
  grade(g) {
    const k = this.fx.glow;
    if (!g || !(k > 0.001)) return g;
    const G = this.G || (this.G = {});
    Object.assign(G, g);
    const sw = this.fx.swell || SWELL;
    for (const key in sw) if (Number.isFinite(g[key]) && Number.isFinite(sw[key])) G[key] = Math.max(0, g[key] * (1 + sw[key] * k));
    return G;
  }

  // QA surface (window.__v5.stages)
  state() {
    return {
      cur: this.cur ? this.cur.i : -1, phase: this.cur ? this.cur.state : '', n: this.count(), total: this.total,
      passed: this.list.map((s) => s.passed), how: this.list.map((s) => s.how), ids: this.list.map((s) => s.id),
      push: +this.push.toFixed(2), charge: +this.charge.toFixed(2),
      stage: this.list.map((s) => { const p = s.gate ? null : this.par(s); return p ? +(+p[s.param] || 0).toFixed(3) : null; }),
    };
  }
  ringXY() { return [Math.round(this.xy[0]), Math.round(this.xy[1])]; }
  stageY(i) { const s = this.list[i]; return s && !s.gate ? this.yOf(s) : null; }
  // jump to stage i's point and hold there (tests, deep links). A passed stage never re-locks: returns false
  arm(i) {
    const s = this.list[i];
    if (this.dead || !s || s.gate || s.passed || !this.S || !this.S.lit) return false;
    if (this.cur && this.cur !== s) this.release(this.cur, 'jump');
    const y = this.yOf(s);
    this.d.lenis.scrollTo(y, { force: true, immediate: true });
    this.S.ys = y; // no fly-through from the old position
    this.prevU = y / Math.max(1, this.d.film.vh);
    if (this.cur !== s) this.grab(s);
    return true;
  }

  dispose() {
    if (this.cur) { try { this.release(this.cur, 'jump'); } catch (e) { /* ignore */ } }
    this.dead = true;
    this.body.classList.remove('stg-on');
    for (const el of [this.ring, this.counter, this.live]) { try { el.remove(); } catch (e) { /* ignore */ } }
  }
}
